// BlockersPanel — Phase 3: Production readiness blockers display
// Ranked list with simulate-resolution capability
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Checkbox,
  Button,
  Chip,
  Skeleton,
  Collapse,
  IconButton,
  Tooltip,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  Error as CriticalIcon,
  Warning as HighIcon,
  Info as MediumIcon,
  CheckCircleOutline as LowIcon,
  ExpandMore as ExpandIcon,
  PlayArrow as SimulateIcon,
  TrendingUp as ImprovementIcon,
} from '@mui/icons-material';
import { useSimulateResolution } from '../../../hooks/useBomIntelligence';
import { colors } from '../../../theme/analyticsColors';
import type { ReadinessBlocker } from '../../../types';

interface BlockersPanelProps {
  bomId: string;
  blockers: ReadinessBlocker[];
  currentScore: number;
  loading?: boolean;
}

const severityConfig = {
  CRITICAL: { icon: CriticalIcon, color: colors.chart.error, bg: alpha(colors.chart.error, 0.06), label: 'Critical' },
  HIGH: { icon: HighIcon, color: colors.warning, bg: alpha(colors.warning, 0.06), label: 'High' },
  MEDIUM: { icon: MediumIcon, color: colors.chart.warning, bg: alpha(colors.chart.warning, 0.06), label: 'Medium' },
  LOW: { icon: LowIcon, color: colors.chart.primary, bg: alpha(colors.chart.primary, 0.06), label: 'Low' },
};

const effortBadge = {
  LOW: { label: 'Quick Fix', color: colors.chart.success },
  MEDIUM: { label: 'Moderate', color: colors.chart.warning },
  HIGH: { label: 'Major Effort', color: colors.chart.error },
};

const BlockersPanel: React.FC<BlockersPanelProps> = ({
  bomId,
  blockers,
  currentScore,
  loading = false,
}) => {
  const [selectedBlockers, setSelectedBlockers] = useState<Set<string>>(new Set());
  const [expandedBlocker, setExpandedBlocker] = useState<string | null>(null);
  const simulateMutation = useSimulateResolution();

  const handleToggle = (blockerId: string) => {
    setSelectedBlockers((prev) => {
      const next = new Set(prev);
      if (next.has(blockerId)) next.delete(blockerId);
      else next.add(blockerId);
      return next;
    });
  };

  const handleSimulate = () => {
    if (selectedBlockers.size === 0) return;
    simulateMutation.mutate({
      bomId,
      blockerIds: Array.from(selectedBlockers),
    });
  };

  if (loading) {
    return (
      <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Skeleton variant="text" width={140} height={28} sx={{ mb: 2 }} />
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={56} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!blockers || blockers.length === 0) {
    return (
      <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: colors.primary, mb: 1 }}>
            Blockers
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: colors.chart.success, fontWeight: 500 }}>
            No blockers identified — BOM is production-ready!
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const projectedScore = simulateMutation.data?.projectedScore;
  const scoreImprovement = projectedScore != null ? projectedScore - currentScore : null;

  return (
    <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: colors.primary }}>
              Blockers
            </Typography>
            <Chip
              label={blockers.length}
              size="small"
              sx={{ bgcolor: alpha(colors.chart.error, 0.06), color: colors.chart.error, fontWeight: 600, fontSize: '0.7rem', height: 22 }}
            />
          </Box>

          {selectedBlockers.size > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {scoreImprovement != null && (
                <Chip
                  icon={<ImprovementIcon sx={{ fontSize: 14 }} />}
                  label={`+${Math.round(scoreImprovement)} pts → ${Math.round(projectedScore!)}`}
                  size="small"
                  sx={{
                    bgcolor: alpha(colors.chart.success, 0.08),
                    color: colors.success,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    '& .MuiChip-icon': { color: colors.success },
                  }}
                />
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={simulateMutation.isPending ? <CircularProgress size={14} /> : <SimulateIcon />}
                disabled={simulateMutation.isPending}
                onClick={handleSimulate}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  borderColor: colors.chart.quaternary,
                  color: colors.primary,
                }}
              >
                Simulate ({selectedBlockers.size})
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
          {blockers.map((blocker) => {
            const sev = severityConfig[blocker.severity] || severityConfig.MEDIUM;
            const SevIcon = sev.icon;
            const effort = effortBadge[blocker.effortLevel] || effortBadge.MEDIUM;
            const isExpanded = expandedBlocker === blocker.blockerId;
            const isSelected = selectedBlockers.has(blocker.blockerId);

            return (
              <Box
                key={blocker.blockerId}
                sx={{
                  border: '1px solid',
                  borderColor: isSelected ? alpha(sev.color, 0.4) : colors.border,
                  borderRadius: 1.5,
                  mb: 1,
                  bgcolor: isSelected ? alpha(sev.color, 0.04) : 'transparent',
                  transition: 'all 0.15s',
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1, gap: 1, cursor: 'pointer' }}
                  onClick={() => setExpandedBlocker(isExpanded ? null : blocker.blockerId)}
                >
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleToggle(blocker.blockerId)}
                    sx={{ p: 0.5, color: colors.chart.quaternary }}
                  />
                  <SevIcon sx={{ fontSize: 18, color: sev.color }} />
                  <Typography sx={{ flex: 1, fontSize: '0.8rem', fontWeight: 500, color: colors.primary }} noWrap>
                    {blocker.description}
                  </Typography>
                  <Tooltip title={`Impact: ${blocker.impactPoints} pts`}>
                    <Chip
                      label={`-${blocker.impactPoints}`}
                      size="small"
                      sx={{
                        bgcolor: alpha(sev.color, 0.1),
                        color: sev.color,
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        height: 20,
                        minWidth: 36,
                      }}
                    />
                  </Tooltip>
                  <Chip
                    label={effort.label}
                    size="small"
                    sx={{
                      bgcolor: alpha(effort.color, 0.1),
                      color: effort.color,
                      fontSize: '0.6rem',
                      height: 18,
                      fontWeight: 500,
                    }}
                  />
                  <IconButton size="small" sx={{ p: 0.25 }}>
                    <ExpandIcon
                      sx={{
                        fontSize: 18,
                        color: colors.light,
                        transform: isExpanded ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </Box>

                <Collapse in={isExpanded}>
                  <Box sx={{ px: 2, pb: 1.5, pt: 0.5, borderTop: `1px solid ${colors.surfaceAlt}` }}>
                    <Typography sx={{ fontSize: '0.75rem', color: colors.secondary, mb: 0.75 }}>
                      {blocker.recommendedAction}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={`Type: ${blocker.type.replace(/_/g, ' ')}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20, borderColor: colors.border }}
                      />
                      <Chip
                        label={`${blocker.affectedLineItems.length} item${blocker.affectedLineItems.length !== 1 ? 's' : ''} affected`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20, borderColor: colors.border }}
                      />
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

export default BlockersPanel;
